import { memo, useId, type ReactElement } from 'react';
import { NavLink } from 'react-router';
import type { ConversationItem } from '@chatapp-inbox/shared';
import { Avatar } from './Avatar';
import {
  SEND_FAILURE_LABEL,
  displayName,
  formatListTime,
  hasSendFailure,
  lineName,
  previewText,
  unreadBadgeText,
  unreadLabel,
} from './format';
import styles from './ConversationRow.module.css';

interface ConversationRowProps {
  conversation: ConversationItem;
  /** Query string atual (com o "?"), repassada ao link para o filtro sobreviver à navegação. */
  search: string;
  /** "Hoje" para o rótulo de horário (muda só na virada do dia). */
  today: Date;
  /** Aba "Todas": mostra quem é o responsável. */
  showAssignee: boolean;
  /**
   * Há 2 ou mais números ligados: mostra em qual deles a conversa acontece. Com um só número a
   * informação seria a mesma em toda linha (ruído).
   */
  showLine: boolean;
}

/**
 * Uma conversa na lista. Todo o texto vindo do cliente (nome, mensagem) entra como filho de
 * elemento, nunca como HTML: o React escapa, então `<img onerror>` na mensagem vira só texto.
 */
export const ConversationRow = memo(function ConversationRow({
  conversation,
  search,
  today,
  showAssignee,
  showLine,
}: ConversationRowProps): ReactElement {
  const uid = useId();
  const { contact, lastMessage, unreadCount } = conversation;
  const name = displayName(contact);
  const preview = previewText(lastMessage);
  const failed = hasSendFailure(lastMessage);
  const unread = unreadCount > 0;
  const closed = conversation.status === 'closed';
  const time = formatListTime(conversation.lastMessageAt, today);
  const lineLabel = showLine && conversation.line ? lineName(conversation.line) : null;
  const hasMeta = closed || showAssignee || lineLabel !== null;

  const ids = {
    name: `${uid}-name`,
    unread: `${uid}-unread`,
    failure: `${uid}-failure`,
    preview: `${uid}-preview`,
    time: `${uid}-time`,
    meta: `${uid}-meta`,
  };
  // Nome acessível = contato + não lidas + prévia (a mesma fala da tela, sem repetir o horário).
  // Horário e situação (encerrada/número/responsável) entram como descrição, depois do nome.
  const labelledBy = [ids.name, unread && ids.unread, failed && ids.failure, ids.preview]
    .filter(Boolean)
    .join(' ');
  const describedBy = [time && ids.time, hasMeta && ids.meta].filter(Boolean).join(' ');

  return (
    <li className={styles.row}>
      <NavLink
        to={{ pathname: `/c/${encodeURIComponent(conversation.id)}`, search }}
        className={styles.item}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy || undefined}
        // Lido por `focusConversationInList` (focus.ts): a casca devolve o foco a esta linha.
        data-conversation-id={conversation.id}
      >
        <Avatar contactId={contact.id} name={name} />
        <span className={styles.body}>
          <span className={styles.line}>
            {/* `dir="auto"` nos três textos de terceiros: o cliente pode escrever em árabe ou hebraico, e
                numa linha LTR o corte com reticências mostraria o FIM da frase. */}
            <span id={ids.name} dir="auto" className={unread ? styles.nameUnread : styles.name}>
              {name}
            </span>
            {time && conversation.lastMessageAt && (
              <time
                id={ids.time}
                dateTime={conversation.lastMessageAt}
                className={unread ? styles.timeUnread : styles.time}
              >
                {time}
              </time>
            )}
          </span>
          <span className={styles.line}>
            {failed && (
              <span id={ids.failure} className={styles.failure}>
                {SEND_FAILURE_LABEL}
              </span>
            )}
            <span id={ids.preview} dir="auto" className={styles.preview}>
              {preview}
            </span>
            {unread && (
              <span className={styles.badge}>
                <span aria-hidden="true">{unreadBadgeText(unreadCount)}</span>
                <span id={ids.unread} className="sr-only">
                  {unreadLabel(unreadCount)}
                </span>
              </span>
            )}
          </span>
          {hasMeta && (
            <span id={ids.meta} className={styles.meta}>
              {closed && <span className={styles.closed}>Encerrada</span>}
              {lineLabel !== null && (
                <span dir="auto" className={styles.lineName}>
                  {`Número: ${lineLabel}`}
                </span>
              )}
              {showAssignee && (
                <span dir="auto" className={styles.assignee}>
                  {conversation.assignedTo
                    ? `Responsável: ${conversation.assignedTo.name}`
                    : 'Sem responsável'}
                </span>
              )}
            </span>
          )}
        </span>
      </NavLink>
    </li>
  );
});
